/* DoctorOffers — Offers & Promotions for the doctor portal.
 *
 * iter 98.30 — full rewrite based on the HTML mockup the user supplied.
 * Strict design rules applied:
 *   - NO left-rail accents on stat cards (icon chip top-right instead)
 *   - Centered Dialog modal for offer detail (NOT side drawer)
 *   - Consistent header style (font-display text-2xl, rounded-full sm buttons)
 *   - Teal/emerald palette
 *
 * Layout:
 *   - Header + "Back to dashboard"
 *   - 4-card stat strip (Live · Expiring soon · Saved · Times applied)
 *   - Search + 4-chip filter (All / Live / Upcoming / Expired)
 *   - 2-col layout: offer cards left, side panel right (How it works,
 *     Your savings summary, Coming soon teaser)
 *   - Click a card → centered offer detail Dialog with big discount hero,
 *     promo code copy, details, usage bar, applied cases, eligible tiers, terms
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FilterSearchBar, FilterChipRow } from "@/components/doctor/FilterBar";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
    DropdownMenuRadioGroup, DropdownMenuRadioItem,
    DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
    Sparkle, Copy, Check, Tag, Calendar, ArrowRight, Info, MagnifyingGlass,
    CheckCircle, Clock, TrendUp, Stack, PiggyBank, CalendarBlank, Warning,
    ShieldCheck, Storefront, Plus, X, ArrowsDownUp,
} from "@phosphor-icons/react";
import { toast } from "sonner";


/* ─────────────── Helpers ─────────────── */
const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    } catch { return iso; }
};

/* Accent token → discount hero color + dialog icon palette.
   Driven by index (`i % palette.length`) for deterministic variety
   without admin needing to colour-pick each promo. */
const ACCENT_PALETTE = [
    { topBar: "from-primary to-emerald-400", chip: "bg-primary/10 text-primary",       value: "text-primary",   bigBg: "bg-primary/5" },
    { topBar: "from-amber-500 to-amber-300", chip: "bg-amber-50 text-amber-700",       value: "text-amber-600", bigBg: "bg-amber-50" },
    { topBar: "from-violet-500 to-violet-300", chip: "bg-violet-50 text-violet-700",   value: "text-violet-600",bigBg: "bg-violet-50" },
    { topBar: "from-sky-500 to-sky-300",     chip: "bg-sky-50 text-sky-700",           value: "text-sky-600",   bigBg: "bg-sky-50" },
    { topBar: "from-rose-500 to-rose-300",   chip: "bg-rose-50 text-rose-700",         value: "text-rose-600",  bigBg: "bg-rose-50" },
];
const accentFor = (idx) => ACCENT_PALETTE[idx % ACCENT_PALETTE.length];


const STATUS_BADGE = {
    live:     { label: "Live",      bg: "bg-primary/10 text-primary border-primary/30",   pulse: true },
    upcoming: { label: "Upcoming",  bg: "bg-sky-50 text-sky-700 border-sky-200",          pulse: false },
    ending:   { label: "Ending soon", bg: "bg-amber-50 text-amber-800 border-amber-200",  pulse: false },
    expired:  { label: "Expired",   bg: "bg-secondary text-muted-foreground border-border", pulse: false },
};


/* Tone classes for the StatCard — no left rails, just an icon chip + value */
const TONE = {
    primary: { chip: "bg-primary/10 text-primary",     val: "text-primary" },
    amber:   { chip: "bg-amber-50 text-amber-700",     val: "text-amber-700" },
    violet:  { chip: "bg-violet-50 text-violet-700",   val: "text-violet-700" },
    sky:     { chip: "bg-sky-50 text-sky-700",         val: "text-sky-700" },
    neutral: { chip: "bg-secondary text-foreground/70",val: "text-foreground" },
};

function StatCard({ label, value, sub, tone = "neutral", icon: Icon, subTone, testid }) {
    const t = TONE[tone] || TONE.neutral;
    const subClass = subTone === "success" ? "text-emerald-700"
                   : subTone === "warn"    ? "text-amber-700"
                   : subTone === "danger"  ? "text-rose-700"
                   : "text-muted-foreground";
    return (
        <div
            className="bg-card border border-border rounded-2xl p-4"
            data-testid={testid}
        >
            <div className="flex items-start justify-between gap-2 mb-2">
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
                {Icon && (
                    <span className={`w-7 h-7 rounded-lg grid place-items-center shrink-0 ${t.chip}`}>
                        <Icon size={14} weight="duotone" />
                    </span>
                )}
            </div>
            <div className={`font-display text-2xl font-bold tabular-nums leading-none ${t.val}`}>{value}</div>
            {sub && <div className={`text-[11px] mt-1.5 inline-flex items-center gap-1 ${subClass}`}>{sub}</div>}
        </div>
    );
}


/* ─────────────── Main page ─────────────── */
export default function DoctorOffers() {
    const navigate = useNavigate();
    const [overview, setOverview] = useState({ promotions: [], usage_by_code: {}, total_saved: 0 });
    const [tiers, setTiers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [query, setQuery] = useState("");
    const [sortBy, setSortBy] = useState("newest");
    const [openId, setOpenId] = useState(null);
    const [copiedCode, setCopiedCode] = useState(null);

    useEffect(() => {
        Promise.all([
            api.get("/doctor/offers/overview")
                .then((r) => setOverview(r.data || { promotions: [], usage_by_code: {}, total_saved: 0 }))
                .catch(() => setOverview({ promotions: [], usage_by_code: {}, total_saved: 0 })),
            api.get("/tiers/active")
                .then((r) => setTiers(r.data || []))
                .catch(() => setTiers([])),
        ]).finally(() => setLoading(false));
    }, []);

    const tierName = useMemo(() => {
        const m = {};
        tiers.forEach((t) => { m[t.id] = t.name; });
        return m;
    }, [tiers]);

    const promotions = overview.promotions || [];
    const usageByCode = overview.usage_by_code || {};

    const summary = useMemo(() => {
        const live = promotions.filter((p) => p.status === "live");
        const upcoming = promotions.filter((p) => p.status === "upcoming");
        const ending = live.filter((p) => p.days_left > 0 && p.days_left <= 14);
        const timesApplied = Object.values(usageByCode).reduce((s, u) => s + (u.uses || 0), 0);
        return {
            live: live.length,
            upcoming: upcoming.length,
            ending: ending.length,
            timesApplied,
        };
    }, [promotions, usageByCode]);

    const visiblePromos = useMemo(() => {
        const q = (query || "").trim().toLowerCase();
        const list = promotions.filter((p) => {
            if (filter !== "all" && p.status !== filter) return false;
            if (!q) return true;
            return (
                (p.name || "").toLowerCase().includes(q) ||
                (p.code || "").toLowerCase().includes(q) ||
                (p.description || "").toLowerCase().includes(q)
            );
        });
        /* iter101.87 · Sort applied on the filtered list so counts on the
           chip row stay accurate (they count against the pre-sort pool). */
        const sorters = {
            newest:    (a, b) => new Date(b.valid_from || 0) - new Date(a.valid_from || 0),
            oldest:    (a, b) => new Date(a.valid_from || 0) - new Date(b.valid_from || 0),
            ending:    (a, b) => (a.days_left ?? 9999) - (b.days_left ?? 9999),
            biggest:   (a, b) => (b.discount_value || 0) - (a.discount_value || 0),
            "name-asc":  (a, b) => (a.name || "").localeCompare(b.name || ""),
            "name-desc": (a, b) => (b.name || "").localeCompare(a.name || ""),
        };
        const cmp = sorters[sortBy] || sorters.newest;
        return [...list].sort(cmp);
    }, [promotions, filter, query, sortBy]);

    const copy = async (code) => {
        try {
            await navigator.clipboard.writeText(code);
            setCopiedCode(code);
            toast.success(`Code ${code} copied`);
            setTimeout(() => setCopiedCode(null), 1800);
        } catch {
            toast.error("Couldn't copy code");
        }
    };

    const openPromo = promotions.find((p) => p.id === openId);

    return (
        <div data-testid="doctor-offers-page" className="space-y-5">
            {/* ─── HEADER — matches /doctor/cases + /doctor/patients exactly ─── */}
            <div data-testid="offers-header">
                <h1 className="font-display text-2xl font-bold tracking-tight">Offers</h1>
                <p className="text-xs text-muted-foreground mt-1">
                    Current promotions you can apply to new aligner cases. Codes auto-suggest inside the case wizard too.
                </p>
            </div>

            {/* ─── STAT STRIP — 4 cards, NO left rails ─── */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="offers-summary">
                <StatCard
                    label="Live Offers"
                    value={summary.live}
                    sub={<><CheckCircle size={11} weight="fill" /> Active right now</>}
                    subTone={summary.live > 0 ? "success" : undefined}
                    tone="primary"
                    icon={Sparkle}
                    testid="offers-stat-live"
                />
                <StatCard
                    label="Expiring Soon"
                    value={summary.ending}
                    sub={<><Clock size={11} weight="bold" /> Within 14 days</>}
                    subTone={summary.ending > 0 ? "warn" : undefined}
                    tone={summary.ending > 0 ? "amber" : "neutral"}
                    icon={Clock}
                    testid="offers-stat-ending"
                />
                <StatCard
                    label="Total Saved (You)"
                    value={fmtINR(overview.total_saved)}
                    sub={<><TrendUp size={11} weight="bold" /> {summary.timesApplied === 1 ? "Across 1 case" : `Across ${summary.timesApplied} cases`}</>}
                    subTone={overview.total_saved > 0 ? "success" : undefined}
                    tone="violet"
                    icon={PiggyBank}
                    testid="offers-stat-saved"
                />
                <StatCard
                    label="Times Applied"
                    value={summary.timesApplied}
                    sub="Codes used on cases"
                    tone="sky"
                    icon={Stack}
                    testid="offers-stat-applied"
                />
            </div>

            {/* ─── CONTROLS ─── iter100.197 · Shared FilterSearchBar +
                FilterChipRow primitives from components/doctor/FilterBar.jsx.
                Locks height + font-size + placeholder styling across
                every doctor-portal list surface. iter101.87 · Sort
                dropdown lives in the rightSlot to match /doctor/cases. */}
            <FilterSearchBar
                query={query}
                setQuery={setQuery}
                placeholder="Search offer name, code…"
                searchTestid="offers-search"
                testid="offers-controls"
                rightSlot={
                    <OffersSortMenu sortBy={sortBy} setSortBy={setSortBy} />
                }
            />
            <FilterChipRow
                value={filter}
                onChange={setFilter}
                testid="offers-filter-chips"
                chipTestidPrefix="offers-filter"
                chips={[
                    { id: "all",      label: "All offers", count: promotions.length,                                        tone: "primary" },
                    { id: "live",     label: "Live",       count: promotions.filter((p) => p.status === "live").length,     tone: "primary" },
                    { id: "upcoming", label: "Upcoming",   count: promotions.filter((p) => p.status === "upcoming").length, tone: "sky"     },
                    { id: "expired",  label: "Expired",    count: promotions.filter((p) => p.status === "expired").length,  tone: "slate"   },
                ]}
            />

            {/* ─── TWO-COL LAYOUT ─── */}
            <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-5 items-start">
                {/* LEFT: offer cards */}
                <div className="flex flex-col gap-3" data-testid="offers-grid">
                    {loading ? (
                        <div className="text-center text-muted-foreground text-sm py-16 bg-card border border-border rounded-2xl">
                            Loading offers…
                        </div>
                    ) : visiblePromos.length === 0 ? (
                        <EmptyState filter={filter} query={query} />
                    ) : (
                        visiblePromos.map((p, idx) => (
                            <OfferCard
                                key={p.id}
                                promo={p}
                                accent={accentFor(idx)}
                                tierNames={(p.applicable_tier_ids || []).map((id) => tierName[id]).filter(Boolean)}
                                onOpen={() => setOpenId(p.id)}
                                onCopy={(e) => { e.stopPropagation(); copy(p.code); }}
                                copied={copiedCode === p.code}
                                usage={usageByCode[p.code]}
                            />
                        ))
                    )}
                </div>

                {/* RIGHT: side panel */}
                <SidePanel
                    upcomingPromos={promotions.filter((p) => p.status === "upcoming").slice(0, 4)}
                    usageByCode={usageByCode}
                    totalSaved={overview.total_saved}
                    onOpenPromo={(id) => setOpenId(id)}
                />
            </div>

            {/* ─── OFFER DETAIL DIALOG (centered modal — NOT a side drawer) ─── */}
            <OfferDetailDialog
                promo={openPromo}
                accent={openPromo ? accentFor(promotions.findIndex((x) => x.id === openPromo.id)) : null}
                tierNames={openPromo ? (openPromo.applicable_tier_ids || []).map((id) => tierName[id]).filter(Boolean) : []}
                usage={openPromo ? usageByCode[openPromo.code] : null}
                onClose={() => setOpenId(null)}
                onCopy={(code) => copy(code)}
                copied={!!openPromo && copiedCode === openPromo.code}
                onUseOnCase={(code) => {
                    navigate(`/doctor/cases/new?promo=${encodeURIComponent(code)}`);
                }}
            />
        </div>
    );
}


/* ─────────────── Offer card ─────────────── */
function OfferCard({ promo, accent, tierNames, onOpen, onCopy, copied, usage }) {
    const isExpired = promo.status === "expired";
    const isUpcoming = promo.status === "upcoming";
    const isPct = promo.discount_type === "percentage";
    const dValue = isPct ? `${promo.discount_value}%` : fmtINR(promo.discount_value);
    const dLabel = "OFF LAB CHARGE";

    // Determine status badge variant
    let badgeKey = promo.status;
    if (promo.status === "live" && promo.days_left > 0 && promo.days_left <= 14) badgeKey = "ending";
    const badge = STATUS_BADGE[badgeKey] || STATUS_BADGE.live;

    const totalDays = Math.max(promo.total_days || 1, 1);
    const pct = isExpired ? 0 : Math.max(0, Math.min(100, Math.round((promo.days_left / totalDays) * 100)));
    const progressTone = isExpired
        ? "bg-rose-400"
        : (pct <= 15 ? "bg-amber-500" : "bg-primary");

    return (
        <button
            type="button"
            onClick={onOpen}
            data-testid={`offer-card-${promo.code}`}
            className={`relative bg-card border border-border rounded-2xl overflow-hidden text-left transition-all hover:shadow-md hover:border-primary/30 ${
                isExpired ? "opacity-60" : ""
            }`}
        >
            {/* Top accent bar (4px gradient — TOP, not left rail) */}
            <div className={`h-1 w-full bg-gradient-to-r ${accent.topBar}`} />

            <div className="p-5">
                {/* Top row: status + title + discount hero */}
                <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex-1 min-w-0">
                        <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border mb-2 ${badge.bg}`}>
                            {badge.pulse && <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />}
                            {badgeKey === "ending"
                                ? `Ending soon · ${promo.days_left}d left`
                                : badgeKey === "live"
                                    ? `Live · ${promo.days_left} days left`
                                    : badgeKey === "upcoming"
                                        ? `Starts in ${promo.days_left}d`
                                        : badge.label}
                        </span>
                        <div className="font-display text-base font-bold leading-tight">{promo.name}</div>
                        {promo.description && (
                            <div className="text-xs text-muted-foreground mt-1 leading-relaxed line-clamp-2">{promo.description}</div>
                        )}
                    </div>
                    <div className={`shrink-0 text-center rounded-lg px-4 py-3 ${accent.bigBg}`}>
                        <div className={`font-display text-2xl font-bold leading-none ${accent.value}`}>{dValue}</div>
                        <div className="text-[9px] font-bold text-muted-foreground tracking-wider mt-1">{dLabel}</div>
                    </div>
                </div>

                {/* Meta row */}
                <div className="flex flex-wrap gap-x-4 gap-y-1.5 py-3 border-y border-border/60 text-[11px] text-muted-foreground">
                    <span className="inline-flex items-center gap-1.5">
                        <Tag size={11} weight="bold" />
                        {tierNames.length === 0 ? "All tiers" : tierNames.join(" · ")}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                        <Calendar size={11} weight="bold" />
                        {fmtDate(promo.valid_from)} → {fmtDate(promo.valid_until)}
                    </span>
                </div>

                {/* Progress bar (days remaining) — only when live or upcoming */}
                {!isExpired && (
                    <div className="mt-3">
                        <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
                            <span>{isUpcoming ? "Launches in" : "Time remaining"}</span>
                            <span className="tabular-nums">{pct}%</span>
                        </div>
                        <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                            <div className={`h-full ${progressTone} rounded-full transition-all`} style={{ width: `${pct}%` }} />
                        </div>
                    </div>
                )}

                {/* Tiers chips + used badge */}
                <div className="flex flex-wrap items-center gap-1.5 mt-3">
                    {tierNames.map((t) => (
                        <span
                            key={t}
                            className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-secondary border border-border text-muted-foreground"
                        >
                            <CheckCircle size={9} weight="fill" className="text-primary" /> {t}
                        </span>
                    ))}
                    {usage && usage.uses > 0 && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-primary/10 border border-primary/30 text-primary">
                            <Check size={9} weight="bold" /> Used {usage.uses}×
                        </span>
                    )}
                </div>

                {/* Promo code row */}
                <div
                    role="button"
                    tabIndex={0}
                    onClick={onCopy}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onCopy(e); }}
                    className={`mt-3 flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-lg border bg-secondary/50 hover:bg-primary/5 hover:border-primary/40 transition-colors cursor-pointer ${
                        copied ? "border-primary/40 bg-primary/5" : "border-border"
                    }`}
                    data-testid={`offer-copy-${promo.code}`}
                >
                    <div className="flex items-center gap-2 text-primary font-bold tracking-wider text-sm">
                        <Tag size={13} weight="duotone" />
                        <span className="font-mono">{promo.code}</span>
                    </div>
                    <span className={`text-[10px] inline-flex items-center gap-1 ${copied ? "text-primary font-semibold" : "text-muted-foreground"}`}>
                        {copied ? <><Check size={11} weight="bold" /> Copied!</> : <><Copy size={11} weight="bold" /> Tap to copy</>}
                    </span>
                </div>
            </div>
        </button>
    );
}


/* ─────────────── Side panel (right column) ─────────────── */
function SidePanel({ upcomingPromos, usageByCode, totalSaved, onOpenPromo }) {
    const usageList = Object.entries(usageByCode).map(([code, u]) => ({ code, ...u }))
        .sort((a, b) => (b.total_saved || 0) - (a.total_saved || 0));
    return (
        <div className="flex flex-col gap-4 xl:sticky xl:top-6" data-testid="offers-side-panel">
            {/* HOW IT WORKS */}
            <div className="bg-card border border-border rounded-2xl overflow-hidden" data-testid="offers-how-it-works">
                <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                    <Info size={16} weight="duotone" className="text-primary" />
                    <div className="text-xs font-semibold">How it works</div>
                </div>
                <div className="p-4 space-y-3">
                    {[
                        { n: 1, head: "Browse live offers", body: "Admin-published promotions appear here automatically whenever they go live." },
                        { n: 2, head: "Pick a code when creating a case", body: "Eligible codes show as one-tap chips above the promo input in the case wizard." },
                        { n: 3, head: "Discount applied to lab charge", body: "The reduced lab fee is billed when aligners ship to your clinic." },
                        { n: 4, head: "Terms set by Invisique™", body: "Promos may apply to specific tiers or case counts only. Check each card for eligibility." },
                    ].map((s) => (
                        <div key={s.n} className="flex items-start gap-2.5">
                            <span className="w-5 h-5 rounded-full bg-primary text-white text-[10px] font-bold grid place-items-center shrink-0 mt-0.5">{s.n}</span>
                            <div className="text-[11px] text-muted-foreground leading-relaxed">
                                <strong className="text-foreground font-semibold">{s.head}</strong> — {s.body}
                            </div>
                        </div>
                    ))}
                    <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-primary/10 border border-primary/30">
                        <ShieldCheck size={13} weight="duotone" className="text-primary mt-0.5 shrink-0" />
                        <p className="text-[11px] text-primary leading-relaxed">
                            Discounts are applied automatically at billing — you never need to chase a refund.
                        </p>
                    </div>
                </div>
            </div>

            {/* YOUR SAVINGS TO DATE */}
            {usageList.length > 0 && (
                <div className="bg-card border border-border rounded-2xl overflow-hidden" data-testid="offers-savings-card">
                    <div className="px-4 py-3 bg-primary/10 border-b border-primary/30 flex items-center gap-2">
                        <PiggyBank size={16} weight="duotone" className="text-primary" />
                        <div className="text-xs font-semibold text-primary">Your savings to date</div>
                    </div>
                    <div className="p-4">
                        {usageList.map((u) => (
                            <div
                                key={u.code}
                                className="flex items-center justify-between py-1.5 border-b border-border last:border-b-0 text-xs"
                            >
                                <span className="text-muted-foreground truncate">
                                    <span className="font-mono text-foreground/80">{u.code}</span> — {u.uses} use{u.uses === 1 ? "" : "s"}
                                </span>
                                <strong className="font-semibold tabular-nums">{fmtINR(u.total_saved)}</strong>
                            </div>
                        ))}
                        <div className="flex items-center justify-between pt-3 mt-1 border-t border-primary/30 text-sm font-bold text-primary">
                            <span>Total saved</span>
                            <span className="tabular-nums">{fmtINR(totalSaved)}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* COMING SOON */}
            {upcomingPromos.length > 0 && (
                <div className="bg-card border border-border rounded-2xl overflow-hidden" data-testid="offers-upcoming-card">
                    <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                        <CalendarBlank size={16} weight="duotone" className="text-sky-600" />
                        <div className="text-xs font-semibold">Coming soon</div>
                    </div>
                    <div>
                        {upcomingPromos.map((p) => {
                            const v = p.discount_type === "percentage" ? `${p.discount_value}%` : fmtINR(p.discount_value);
                            return (
                                <button
                                    type="button"
                                    key={p.id}
                                    onClick={() => onOpenPromo?.(p.id)}
                                    className="w-full flex items-center gap-2.5 px-4 py-3 border-b border-border last:border-b-0 hover:bg-secondary/40 transition-colors text-left"
                                    data-testid={`offers-upcoming-${p.code}`}
                                >
                                    <span className="w-8 h-8 rounded-lg bg-sky-50 text-sky-600 grid place-items-center shrink-0">
                                        <Sparkle size={14} weight="duotone" />
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-xs font-medium truncate">{p.name}</div>
                                        <div className="text-[10px] text-muted-foreground mt-0.5">Launches {fmtDate(p.valid_from)}</div>
                                    </div>
                                    <div className="text-xs font-bold text-foreground/70 tabular-nums">{v}</div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}


/* ─────────────── Offer detail Dialog (centered modal) ─────────────── */
function OfferDetailDialog({ promo, accent, tierNames, usage, onClose, onCopy, copied, onUseOnCase }) {
    if (!promo) return null;
    const isPct = promo.discount_type === "percentage";
    const dValue = isPct ? `${promo.discount_value}%` : fmtINR(promo.discount_value);
    const dLabel = "OFF LAB CHARGE";
    const usePct = usage && usage.uses ? Math.min(100, Math.round((usage.uses / Math.max(usage.uses, 1)) * 100)) : 0;

    return (
        <Dialog open={!!promo} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="offer-detail-dialog">
                <DialogHeader>
                    <div className="flex items-start gap-3">
                        <span className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${accent?.chip || "bg-primary/10 text-primary"}`}>
                            <Tag size={18} weight="duotone" />
                        </span>
                        <div className="flex-1 min-w-0">
                            <DialogTitle className="font-display text-base font-bold leading-tight" data-testid="offer-detail-title">
                                {promo.name}
                            </DialogTitle>
                            <div className="text-[11px] text-muted-foreground mt-1">
                                <span className="font-mono">{promo.code}</span>
                                {tierNames.length > 0 && <> · {tierNames.join(" · ")}</>}
                            </div>
                        </div>
                    </div>
                </DialogHeader>

                <div className="space-y-4 mt-2">
                    {/* Big discount hero */}
                    <div className={`text-center rounded-xl py-6 ${accent?.bigBg || "bg-secondary/40"}`}>
                        <div className={`font-display text-5xl font-bold leading-none ${accent?.value || "text-primary"}`}>{dValue}</div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mt-2">{dLabel}</div>
                    </div>

                    {/* Promo code copy row */}
                    <button
                        type="button"
                        onClick={() => onCopy(promo.code)}
                        className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg border bg-secondary/40 hover:bg-primary/5 hover:border-primary/40 transition-colors ${
                            copied ? "border-primary/40 bg-primary/5" : "border-border"
                        }`}
                        data-testid="offer-detail-copy"
                    >
                        <div className="flex items-center gap-2 text-primary font-bold text-sm">
                            <Tag size={14} weight="duotone" />
                            <span className="font-mono">{promo.code}</span>
                        </div>
                        <span className={`text-[11px] inline-flex items-center gap-1 ${copied ? "text-primary font-semibold" : "text-muted-foreground"}`}>
                            {copied ? <><Check size={11} weight="bold" /> Copied!</> : <><Copy size={11} weight="bold" /> Tap to copy</>}
                        </span>
                    </button>

                    {/* Offer details */}
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Offer Details</div>
                        <div className="bg-secondary/40 rounded-lg p-3.5 divide-y divide-border/60">
                            <DetailRow k="Discount type" v={isPct ? "Percentage discount" : "Fixed amount discount"} />
                            <DetailRow k="Applies to" v={tierNames.length === 0 ? "All tiers" : tierNames.join(" · ")} />
                            <DetailRow k="Valid from" v={fmtDate(promo.valid_from)} />
                            <DetailRow k="Valid until" v={fmtDate(promo.valid_until)} />
                            <DetailRow k="Days remaining" v={promo.status === "expired" ? "Expired" : `${promo.days_left} days`} />
                            <DetailRow k="Status" v={STATUS_BADGE[promo.status]?.label || promo.status} />
                        </div>
                    </div>

                    {/* Your usage */}
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Your Usage</div>
                        <div className="bg-secondary/40 rounded-lg p-3.5">
                            <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1.5">
                                <span>{(usage?.uses || 0)} use{(usage?.uses || 0) === 1 ? "" : "s"}</span>
                                <span className="font-semibold text-primary tabular-nums">Saved {fmtINR(usage?.total_saved || 0)}</span>
                            </div>
                            <div className="h-1.5 bg-border rounded-full overflow-hidden">
                                <div className="h-full bg-primary rounded-full" style={{ width: `${usePct}%` }} />
                            </div>
                        </div>
                    </div>

                    {/* Applied cases */}
                    {usage && usage.cases && usage.cases.length > 0 && (
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Cases where applied</div>
                            <div className="bg-secondary/40 rounded-lg p-3 space-y-2">
                                {usage.cases.map((c) => (
                                    <Link
                                        key={c.id}
                                        to={`/doctor/cases/${c.id}`}
                                        onClick={onClose}
                                        className="flex items-center gap-3 hover:bg-card rounded-md px-2 py-1.5 transition-colors"
                                        data-testid={`offer-detail-case-${c.id}`}
                                    >
                                        <span className="font-mono text-[11px] font-semibold bg-card border border-border rounded px-1.5 py-0.5">
                                            {c.code}
                                        </span>
                                        <span className="text-xs flex-1 truncate">{c.patient_name}</span>
                                        <span className="text-xs font-semibold text-primary tabular-nums">−{fmtINR(c.saving)}</span>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Eligible tiers */}
                    {tierNames.length > 0 && (
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Eligible tiers</div>
                            <div className="flex flex-wrap gap-1.5">
                                {tierNames.map((t) => (
                                    <span
                                        key={t}
                                        className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full bg-primary/10 border border-primary/30 text-primary"
                                    >
                                        <CheckCircle size={10} weight="fill" /> {t}
                                    </span>
                                ))}
                            </div>
                            <div className="flex items-start gap-2 mt-2.5 p-2.5 rounded-lg bg-amber-50 border border-amber-200">
                                <Warning size={13} weight="duotone" className="text-amber-700 shrink-0 mt-0.5" />
                                <p className="text-[11px] text-amber-900 leading-relaxed">
                                    This offer applies only to the tiers listed above. Applying to a non-eligible case will show an error in the case wizard.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Terms */}
                    {promo.terms && (
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Terms &amp; Conditions</div>
                            <div className="bg-secondary/40 rounded-lg p-3.5 text-[11px] text-muted-foreground leading-relaxed">
                                {promo.terms}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex gap-2 pt-4 mt-2 border-t border-border">
                    <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 rounded-full"
                        onClick={() => onCopy(promo.code)}
                        data-testid="offer-detail-copy-btn"
                    >
                        <Copy size={12} className="mr-1.5" /> Copy code
                    </Button>
                    {promo.status === "live" && (
                        <Button
                            size="sm"
                            className="flex-1 rounded-full"
                            onClick={() => onUseOnCase(promo.code)}
                            data-testid="offer-detail-use-on-case"
                        >
                            <Plus size={12} className="mr-1.5" /> Use on new case
                        </Button>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}


function DetailRow({ k, v }) {
    return (
        <div className="flex items-center justify-between py-1.5 text-xs">
            <span className="text-muted-foreground">{k}</span>
            <span className="text-foreground font-medium text-right">{v}</span>
        </div>
    );
}


/* ─────────────── Sort menu — mirrors /doctor/cases sort UX ─────────────── */
const OFFERS_SORT_OPTIONS = [
    { id: "newest",    label: "Newest first" },
    { id: "oldest",    label: "Oldest first" },
    { id: "ending",    label: "Ending soonest" },
    { id: "biggest",   label: "Biggest discount first" },
    { id: "name-asc",  label: "Name A → Z" },
    { id: "name-desc", label: "Name Z → A" },
];

function OffersSortMenu({ sortBy, setSortBy }) {
    const label = OFFERS_SORT_OPTIONS.find((s) => s.id === sortBy)?.label || "Sort";
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-border bg-card text-[12px] font-medium text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors"
                    title="Sort offers"
                    data-testid="offers-sort-btn"
                >
                    <ArrowsDownUp size={12} /> {label}
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[180px]">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    Sort by
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuRadioGroup value={sortBy} onValueChange={setSortBy}>
                    {OFFERS_SORT_OPTIONS.map((opt) => (
                        <DropdownMenuRadioItem
                            key={opt.id}
                            value={opt.id}
                            className="text-xs"
                            data-testid={`offers-sort-${opt.id}`}
                        >
                            {opt.label}
                        </DropdownMenuRadioItem>
                    ))}
                </DropdownMenuRadioGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}


/* ─────────────── Empty state ─────────────── */
function EmptyState({ filter, query }) {
    const hasFilter = filter !== "all" || query;
    return (
        <div className="bg-card border border-border rounded-2xl p-12 text-center" data-testid="offers-empty">
            <div className="mx-auto w-12 h-12 rounded-full bg-secondary grid place-items-center mb-3">
                <Sparkle size={20} weight="duotone" className="text-muted-foreground" />
            </div>
            <div className="font-display text-base font-bold">
                {hasFilter ? "No offers match your search" : "No live offers right now"}
            </div>
            <p className="text-xs text-muted-foreground mt-1.5 max-w-md mx-auto leading-relaxed">
                {hasFilter
                    ? "Try clearing the filter or searching by a different name/code."
                    : "When Invisique™ publishes a promotion you'll see it here and as a banner on your dashboard. You can still apply manual codes during case submission if you have one."}
            </p>
        </div>
    );
}
